/**
 * IndexedDB-backed write outbox for offline annotation.
 *
 * The Capacitor mobile bundle wraps ``window.fetch`` (see
 * ``offline-fetch.ts``) so that failed writes to annotation /
 * draft endpoints queue here instead of erroring up the stack. A
 * sync runner drains the queue on reconnect, replaying each
 * request via the unwrapped fetch. Idempotency is guaranteed by
 * the ``unique_id`` UUID the cloud already requires on every row
 * — desktop's ``CloudProxyMiddleware`` uses the same property,
 * see ``project_sync_architecture_2026_04_29.md``.
 *
 * Schema is intentionally tiny: one object store, auto-incrementing
 * primary key, FIFO drain order. Future steps will add task data +
 * media caches in their own object stores within the same DB.
 */

const DB_NAME = "afri-offline";
// v2 added per-project read stores (projects, tasks, annotations,
// drafts) so the Capacitor "Download for offline" action has
// somewhere to write its pull. v3 adds a generic API-response cache
// keyed by URL — used to persist cloud-first endpoints (whoami,
// organizations, …) so they survive an app reload while offline.
// v4 adds a URL-content cache for media/text/csv referenced by
// task.data — the SW media cache works for most files but cross-
// origin opaque responses can't be replayed via XHR; persisting the
// raw bytes in IDB lets offline-fetch synthesize a fresh, non-opaque
// Response for any URL the prewarm grabbed. Keyed by exact URL
// (post-apiUrl rewrite) so the cache key matches what LSF later
// requests. Each row: ``{key, body, contentType, savedAt}``.
// v5 — outbox entries gained ``nextAttemptAt`` (ms epoch, replay is
// skipped until this time) and ``uniqueId`` (UUID stamped into the
// request body so cloud dedupes replays). Both are optional so v4
// rows keep working — the drain runner treats missing ``nextAttemptAt``
// as "eligible now". No new object stores; the version bump only
// forces existing browsers to re-open the DB against the fresh
// upgrade path, which is a no-op today but leaves us a hook to add
// a status index in a later slice.
// v6 — ``outbox_dead`` store: entries whose replay the cloud REJECTED
// (4xx other than 409). Before v6 those were silently deleted — a
// day of field annotations could evaporate if e.g. the owner turned
// ``allow_on_mobile`` off while work was queued. Dead entries keep
// the full original request plus the rejection details so the user
// can review, retry, or discard from the failed-writes UI.
const DB_VERSION = 6;
const STORE = "outbox";
export const STORE_DEAD = "outbox_dead";
export const STORE_PROJECTS = "projects";
export const STORE_TASKS = "tasks";
export const STORE_ANNOTATIONS = "annotations";
export const STORE_DRAFTS = "drafts";
export const STORE_API_CACHE = "api_cache";
export const STORE_URL_CONTENT = "url_content";

export type OutboxEntry = {
  /** Primary key (auto-assigned by IndexedDB). */
  id?: number;
  /** HTTP method — POST / PATCH / PUT / DELETE. */
  method: string;
  /** Absolute or app-relative URL. Stored verbatim, replayed as-is. */
  url: string;
  /**
   * Body, already serialised. JSON for application/json; FormData
   * is normalised to a serialisable object (see offline-fetch.ts).
   * ``null`` for DELETE-style empties.
   */
  body: string | null;
  /** Headers needed at replay (Content-Type, Authorization, etc). */
  headers: Record<string, string>;
  /** ms epoch when the entry was queued. */
  queuedAt: number;
  /** Replay attempts so far — used for backoff. */
  attempts: number;
  /**
   * Earliest ms epoch this entry may be replayed. Set on 5xx / network
   * failure via exponential backoff. Missing (v4 rows) = eligible now.
   * The drain runner skips entries whose ``nextAttemptAt > Date.now()``
   * so a single slow replay doesn't spin the loop.
   */
  nextAttemptAt?: number;
  /**
   * UUID stamped into the request body when it's an annotation /
   * draft POST — the cloud's ``unique_id`` idempotency dedupes
   * replays. Mirrors the desktop ``CloudProxyMiddleware`` pattern
   * (see ``project_sync_architecture_2026_04_29.md``). Kept in a
   * dedicated field (rather than only inside ``body``) so the drain
   * runner can log / diagnose without re-parsing the body.
   */
  uniqueId?: string;
};

/** An outbox entry the cloud rejected on replay (non-409 4xx), moved
 *  to ``outbox_dead`` instead of being deleted. Carries everything
 *  needed to re-queue verbatim plus the rejection for display. */
export type DeadEntry = OutboxEntry & {
  /** ms epoch when the replay was rejected. */
  failedAt: number;
  /** HTTP status the cloud answered with (400/403/404/…). */
  failStatus: number;
  /** First ~500 chars of the response body — usually the DRF error
   *  detail; enough for the review modal without storing megabytes. */
  failDetail: string;
};

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB unavailable"));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
      // v2 read stores. Keys are the cloud's primary keys
      // (project.id, task.id) so a write from JS-side
      // ``downloadProjectForOffline`` and a later read by the
      // offline fetch wrapper line up without translation.
      if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
        db.createObjectStore(STORE_PROJECTS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_TASKS)) {
        const tasks = db.createObjectStore(STORE_TASKS, { keyPath: "id" });
        // Indexed by ``project`` so listing all tasks for a project
        // (the LSF starting screen) is cheap when offline.
        tasks.createIndex("by_project", "project", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_ANNOTATIONS)) {
        const ann = db.createObjectStore(STORE_ANNOTATIONS, { keyPath: "id" });
        ann.createIndex("by_task", "task", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        const drafts = db.createObjectStore(STORE_DRAFTS, { keyPath: "id" });
        drafts.createIndex("by_task", "task", { unique: false });
      }
      // v3 — generic API response cache keyed by URL pathname (no
      // query string). Used to persist cloud-first endpoints —
      // whoami, organizations, etc. — so they survive an app
      // reload while offline. Stored as ``{key, body, savedAt}``;
      // ``body`` is the parsed JSON response.
      if (!db.objectStoreNames.contains(STORE_API_CACHE)) {
        db.createObjectStore(STORE_API_CACHE, { keyPath: "key" });
      }
      // v4 — URL-content cache for media/text/csv referenced by
      // task.data. Populated by ``downloadProjectForOffline``'s
      // prewarm step. Cache key is the exact URL LSF will later
      // request (post apiUrl absolute-rewrite). Each row stores
      // raw bytes + content-type so the offline-fetch fallback can
      // synthesize a faithful Response.
      if (!db.objectStoreNames.contains(STORE_URL_CONTENT)) {
        db.createObjectStore(STORE_URL_CONTENT, { keyPath: "key" });
      }
      // v6 — rejected-replay dead letters (see header note). Same
      // auto-increment shape as the outbox so re-queueing is a move.
      if (!db.objectStoreNames.contains(STORE_DEAD)) {
        db.createObjectStore(STORE_DEAD, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IDB open failed"));
  });
  return dbPromise;
};

/** Generic put/get/getAll for the read stores. Used by
 *  ``offline-sync.ts`` (download path) and ``offline-fetch.ts``
 *  (read fallback, future). Outbox uses the older typed helpers
 *  above — kept to preserve their explicit typing. */
/** Stores whose rows are written from MANY endpoints with different
 *  ``include=`` projections (a project row from the home page's 6-item
 *  list lacks ``allow_on_mobile``; the same row from the projects page
 *  has it). A plain ``put`` REPLACES the row, so a narrower projection
 *  wiped fields a wider one had stored — the projects page then saw
 *  ``allow_on_mobile === undefined`` and showed "no projects" until
 *  the next wide fetch. Merge instead: new keys win, missing keys are
 *  kept from the existing row. */
const MERGE_STORES = new Set([STORE_PROJECTS, STORE_TASKS]);

export const putRecord = async (storeName: string, value: unknown): Promise<void> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const v = value as Record<string, unknown> & { id?: unknown; key?: unknown };
    const keyPath = store.keyPath as string;
    const key = keyPath ? (v?.[keyPath] as IDBValidKey | undefined) : undefined;
    if (MERGE_STORES.has(storeName) && key != null && v && typeof v === "object") {
      const req = store.get(key);
      req.onsuccess = () => {
        const existing = req.result as Record<string, unknown> | undefined;
        const merged = existing && typeof existing === "object" ? { ...existing, ...v } : v;
        // Never let an explicit undefined in the new row erase a
        // stored value (JSON never carries undefined, but guard anyway).
        for (const k of Object.keys(merged))
          if (merged[k] === undefined && existing && k in existing) merged[k] = existing[k];
        store.put(merged as unknown as IDBValidKey);
      };
      req.onerror = () => {
        store.put(value as IDBValidKey);
      };
    } else {
      store.put(value as IDBValidKey);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("put failed"));
  });
};

export const getRecord = async <T>(storeName: string, key: IDBValidKey): Promise<T | undefined> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const r = tx.objectStore(storeName).get(key);
    r.onsuccess = () => resolve((r.result as T) ?? undefined);
    r.onerror = () => reject(r.error ?? new Error("get failed"));
  });
};

export const getAllByIndex = async <T>(storeName: string, indexName: string, key: IDBValidKey): Promise<T[]> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const r = tx.objectStore(storeName).index(indexName).getAll(key);
    r.onsuccess = () => resolve((r.result as T[]) ?? []);
    r.onerror = () => reject(r.error ?? new Error("getAll by index failed"));
  });
};

/** All rows in a store, no index filter. Used by the offline read
 *  fallback for the projects-list endpoint where we want every
 *  project the user previously downloaded. */
export const deleteRecord = async (storeName: string, key: IDBValidKey): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IDB delete failed"));
  });
};

export const getAllRecords = async <T>(storeName: string): Promise<T[]> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const r = tx.objectStore(storeName).getAll();
    r.onsuccess = () => resolve((r.result as T[]) ?? []);
    r.onerror = () => reject(r.error ?? new Error("getAll failed"));
  });
};

const runTx = async <T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | void> => {
  const db = await openDb();
  return new Promise<T | void>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = op(store);
    if (result && "onsuccess" in result) {
      result.onsuccess = () => resolve(result.result as T);
      result.onerror = () => reject(result.error ?? new Error("IDB op failed"));
    } else {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("IDB tx failed"));
    }
  });
};

export const enqueue = async (entry: Omit<OutboxEntry, "id">): Promise<number> => {
  const id = await runTx<IDBValidKey>("readwrite", (s) => s.add(entry));
  return Number(id);
};

/** All pending entries, oldest first. */
export const list = async (): Promise<OutboxEntry[]> => {
  const all = await runTx<OutboxEntry[]>("readonly", (s) => s.getAll());
  return (all as OutboxEntry[]) ?? [];
};

export const remove = (id: number): Promise<void | undefined> =>
  runTx("readwrite", (s) => s.delete(id)) as Promise<void>;

export const update = async (entry: OutboxEntry): Promise<void> => {
  await runTx("readwrite", (s) => s.put(entry));
};

export const count = async (): Promise<number> => {
  const c = await runTx<number>("readonly", (s) => s.count());
  return (c as number) ?? 0;
};

export const clearAll = (): Promise<void | undefined> => runTx("readwrite", (s) => s.clear()) as Promise<void>;

/* ── Dead letters (rejected replays) ─────────────────────────────── */

const runDeadTx = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_DEAD, mode);
    const req = fn(tx.objectStore(STORE_DEAD));
    tx.oncomplete = () => resolve(req ? (req.result as T) : undefined);
    tx.onerror = () => reject(tx.error ?? new Error("IDB dead-letter tx failed"));
  });
};

/** Move a rejected outbox entry into the dead-letter store. The
 *  original outbox ``id`` is dropped (a re-queue gets a fresh one)
 *  but everything else is preserved verbatim for retry. */
export const deadLetter = async (entry: OutboxEntry, info: { status: number; detail: string }): Promise<void> => {
  const { id: _origId, ...rest } = entry;
  await runDeadTx("readwrite", (s) =>
    s.add({
      ...rest,
      failedAt: Date.now(),
      failStatus: info.status,
      failDetail: (info.detail || "").slice(0, 500),
    }),
  );
  if (entry.id != null) await remove(entry.id);
};

export const listDead = async (): Promise<DeadEntry[]> => {
  const all = await runDeadTx<DeadEntry[]>("readonly", (s) => s.getAll());
  return (all as DeadEntry[]) ?? [];
};

export const deadCount = async (): Promise<number> => {
  const c = await runDeadTx<number>("readonly", (s) => s.count());
  return (c as number) ?? 0;
};

export const removeDead = (id: number): Promise<void | undefined> =>
  runDeadTx("readwrite", (s) => s.delete(id)) as Promise<void>;

/** Re-queue a dead letter for another replay attempt (e.g. after the
 *  owner re-enabled mobile for the project). Fresh attempts counter,
 *  no cooldown — the next drain picks it up immediately. */
export const requeueDead = async (id: number): Promise<void> => {
  const dead = (await runDeadTx<DeadEntry>("readonly", (s) => s.get(id))) as DeadEntry | undefined;
  if (!dead) return;
  const { failedAt: _f, failStatus: _s, failDetail: _d, id: _id, ...entry } = dead;
  await enqueue({ ...entry, attempts: 0, nextAttemptAt: undefined });
  await removeDead(id);
};

/** Wipe EVERY per-user IDB store. Call from sign-out so the next
 *  user can't read the previous user's whoami / projects / tasks /
 *  annotations / cached URL bytes / queued outbox writes. The
 *  outbox in particular is dangerous to leak: the next sign-in
 *  with a different account would replay annotations under the
 *  wrong user's JWT.
 *
 *  Best-effort — failure to clear a single store should not block
 *  sign-out. The downstream ``location.replace("/")`` after sign-
 *  out also restarts the WebView, which forces fresh state. */
export const clearAllOfflineStores = async (): Promise<void> => {
  const stores = [
    STORE,
    STORE_DEAD,
    STORE_PROJECTS,
    STORE_TASKS,
    STORE_ANNOTATIONS,
    STORE_DRAFTS,
    STORE_API_CACHE,
    STORE_URL_CONTENT,
  ];
  try {
    const db = await openDb();
    await Promise.all(
      stores.map(
        (name) =>
          new Promise<void>((resolve) => {
            try {
              const tx = db.transaction(name, "readwrite");
              tx.objectStore(name).clear();
              tx.oncomplete = () => resolve();
              tx.onerror = () => resolve();
            } catch (_) {
              resolve();
            }
          }),
      ),
    );
  } catch (_) {
    /* swallow — sign-out continues */
  }
};
